import { axiosGet } from "../../utils/Axios";
import { getFillListUrl, inactiveClientActionID } from "../../utils/constants";

export const GUEST_ADDED_TOAST_MESSAGE =
    "User is added. Click Save to share and send an invitation.";

export const isExternalClientEmail = (email) => {
    const trimmed = String(email || "").trim().toLowerCase();
    return trimmed.includes("@") && !trimmed.includes("@colliers");
};

export const isClientDashboardUser = (user) => {
    if (!user) return false;
    const azureGroup = String(user.azureGroup || "").trim();
    const azureGroupID = String(user.azureGroupID || "").trim();
    const isAdGroup =
        Number(user.adGroupID) > 0 ||
        azureGroup.length > 0 ||
        (azureGroupID.length > 0 && azureGroupID !== "0");
    if (isAdGroup) return false;
    if (Number(user.dashboardRoleID) === 2 || Number(user.roleID) === 2) {
        return true;
    }
    return isExternalClientEmail(
        user.emailAddress || user.email || user.userPrincipalName
    );
};

export const resolveDashboardRoleID = (email) =>
    String(email || "").toLowerCase().includes("@colliers") ? 4 : 2;

export const resolveDashboardUserRoleID = (user) => {
    if (isClientDashboardUser(user)) {
        return 2;
    }
    return Number(user?.dashboardRoleID) || 4;
};

export const buildDashboardUserFromSearch = (user) => {
    const userEmail = user?.userPrincipalName || user?.email || "";
    return {
        displayName: user?.displayName || user?.firstName,
        emailAddress: userEmail,
        userID: user?.userID || "",
        validUntil: user?.validUntil,
        rlsName: "",
        azureGroupID: "",
        azureGroupName: "",
        dashboardRoleID: isClientDashboardUser(user) ? 2 : resolveDashboardRoleID(userEmail),
        adGroupID: "",
        isDeleted: false
    };
};

export const findExistingDashboardUser = (groupList, email) => {
    const normalizedEmail = String(email || "").toLowerCase();
    return (groupList || []).find(
        (existingUser) => String(existingUser?.emailAddress || "").toLowerCase() === normalizedEmail
    );
};

export const shouldCheckInactiveBeforeAdding = (newUser, existingUser) =>
    (!existingUser || Boolean(existingUser.isDeleted)) &&
    newUser?.dashboardRoleID === 2 &&
    Boolean(newUser?.emailAddress);

export const partitionInvitedUsers = (groupList, candidateUsers) => {
    const activeExistingEmails = new Set(
        (groupList || [])
            .filter((user) => !user?.isDeleted)
            .map((user) => String(user?.emailAddress || "").toLowerCase())
    );
    const duplicateNewUsers = (candidateUsers || []).filter((user) =>
        activeExistingEmails.has(String(user?.emailAddress || "").toLowerCase())
    );
    const usersToAdd = (candidateUsers || []).filter(
        (user) => !activeExistingEmails.has(String(user?.emailAddress || "").toLowerCase())
    );
    return { duplicateNewUsers, usersToAdd };
};

export const mergeInvitedUsersIntoGroupList = (prevList, newUsers, rlsField = "rlsName") => {
    const safePrev = prevList || [];
    const safeNew = newUsers || [];
    const updatedList = safePrev.map((existing) => {
        const matched = safeNew.find(
            (u) =>
                String(u?.emailAddress || "").toLowerCase() ===
                String(existing?.emailAddress || "").toLowerCase()
        );
        if (!matched || !existing?.isDeleted) {
            return existing;
        }
        return {
            ...existing,
            isDeleted: false,
            validUntil: matched.validUntil || existing.validUntil,
            [rlsField]: matched[rlsField] || existing[rlsField]
        };
    });
    const existingEmails = new Set(
        safePrev.map((u) => String(u?.emailAddress || "").toLowerCase())
    );
    const usersToAppend = safeNew.filter(
        (u) => !existingEmails.has(String(u?.emailAddress || "").toLowerCase())
    );
    return [...updatedList, ...usersToAppend];
};

export const getInactiveClientEmails = async (emails) => {
    const cleanedEmails = (emails || [])
        .map((email) => String(email || "").trim())
        .filter(Boolean);
    if (!cleanedEmails.length) {
        return [];
    }

    try {
        const response = await axiosGet(getFillListUrl, {
            actionID: inactiveClientActionID,
            valueID: null,
            keyValue: cleanedEmails.join(",")
        });
        if (!response || !Array.isArray(response)) {
            return [];
        }
        const normalizedEmails = new Set(cleanedEmails.map((email) => email.toLowerCase()));
        return response
            .map((item) =>
                String(item?.description || item?.emailAddress || item?.email || "")
                    .trim()
                    .toLowerCase()
            )
            .filter((candidate) => Boolean(candidate) && normalizedEmails.has(candidate));
    } catch (error) {
        console.error("Error checking inactive client status:", error);
        return [];
    }
};

export const hasInactiveClientEmails = async (emails) =>
    (await getInactiveClientEmails(emails)).length > 0;

export const partitionUsersByInactiveEmails = (newUsers, inactiveEmails) => {
    const inactiveSet = new Set(
        (inactiveEmails || [])
            .map((email) => String(email || "").trim().toLowerCase())
            .filter(Boolean)
    );
    const safeUsers = newUsers || [];
    return {
        activeUsers: safeUsers.filter(
            (user) => !inactiveSet.has(String(user?.emailAddress || "").trim().toLowerCase())
        ),
        inactiveUsers: safeUsers.filter((user) =>
            inactiveSet.has(String(user?.emailAddress || "").trim().toLowerCase())
        )
    };
};

export const splitUsersByInactiveStatus = async (emails, newUsers) => {
    const inactiveEmails = await getInactiveClientEmails(emails);
    return partitionUsersByInactiveEmails(newUsers, inactiveEmails);
};
